/**
 * Hook to fetch portfolio sections from API
 * Used by Index (gallery) and Portfolio page
 * Solo incluye imágenes Cloudinary (para watermark)
 *
 * Performance (AR BaseTemplate / 4G):
 *   - memory + sessionStorage SWR so Index never blocks first paint on cold
 *     portfolio download
 *   - short timeout; soft browser cache (API max-age)
 *   - `enabled: false` skips network when home is Mux-only (DualCTA / Invitacion)
 */

import { useState, useEffect, useMemo } from "react";
import { parsePortfolioUrl } from "@/config/portfolioConfig";
import { isCloudinaryImage } from "@/lib/cloudinaryUtils";
import type { PortfolioSection } from "@/config/portfolioConfig";

const CLIENT_CACHE_TTL_MS = 60_000;
const CACHE_KEY = "aaf_portfolio_sections_v1";
const FETCH_TIMEOUT_MS = 2500;

type CacheBlob = {
  at: number;
  sections: PortfolioSection[];
  portfolioPublicEnabled: boolean;
};

let memCache: CacheBlob | null = null;
let inflight: Promise<CacheBlob | null> | null = null;

function readClientCache(): CacheBlob | null {
  if (memCache && Date.now() - memCache.at < CLIENT_CACHE_TTL_MS) return memCache;
  if (typeof sessionStorage === "undefined") return null;
  try {
    const raw = sessionStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CacheBlob;
    if (!parsed || !Array.isArray(parsed.sections) || typeof parsed.at !== "number") {
      return null;
    }
    if (Date.now() - parsed.at > CLIENT_CACHE_TTL_MS * 4) return null;
    memCache = parsed;
    return parsed;
  } catch {
    return null;
  }
}

function writeClientCache(blob: CacheBlob): void {
  memCache = blob;
  if (typeof sessionStorage === "undefined") return;
  try {
    sessionStorage.setItem(CACHE_KEY, JSON.stringify(blob));
  } catch {
    /* quota */
  }
}

async function fetchPortfolioShared(forceRefresh: boolean): Promise<CacheBlob | null> {
  if (!forceRefresh && inflight) return inflight;

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);

  const run = (async (): Promise<CacheBlob | null> => {
    try {
      const res = await fetch("/api/portfolio", {
        // Soft browser cache; API exposes short max-age for 4G reloads.
        cache: forceRefresh ? "no-store" : "default",
        signal: controller.signal,
      });
      if (!res.ok) return null;
      const data = await res.json();
      if (data.portfolioPublicEnabled === false) {
        const blob: CacheBlob = {
          at: Date.now(),
          sections: [],
          portfolioPublicEnabled: false,
        };
        writeClientCache(blob);
        return blob;
      }
      const raw = data.sections || [];
      const normalized: PortfolioSection[] = raw.map(
        (s: { id?: string; title?: string; urls?: unknown }) => ({
          id: s.id,
          title: s.title ?? "Sección",
          urls: Array.isArray(s.urls)
            ? s.urls.filter(
                (u): u is string => typeof u === "string" && u.trim().length > 0,
              )
            : [],
        }),
      );
      const blob: CacheBlob = {
        at: Date.now(),
        sections: normalized,
        portfolioPublicEnabled: true,
      };
      writeClientCache(blob);
      return blob;
    } catch (error: unknown) {
      const isAbort = error instanceof Error && error.name === "AbortError";
      if (!isAbort) {
        console.warn(
          "[usePortfolioSections] Fetch error:",
          error instanceof Error ? error.message : String(error),
        );
      } else {
        console.warn(
          `[usePortfolioSections] Request timeout after ${FETCH_TIMEOUT_MS}ms, using cache/empty`,
        );
      }
      return readClientCache();
    } finally {
      clearTimeout(timeoutId);
    }
  })();

  if (!forceRefresh) {
    const tracked = run.finally(() => {
      if (inflight === tracked) inflight = null;
    });
    inflight = tracked;
    return tracked;
  }
  return run;
}

export function usePortfolioSections(
  forceRefresh = false,
  options?: { enabled?: boolean },
) {
  const enabled = options?.enabled !== false;
  const cached = enabled ? readClientCache() : null;
  const [sections, setSections] = useState<PortfolioSection[]>(
    cached?.sections ?? [],
  );
  // Warm cache → first paint not blocked (AR BaseTemplate win).
  const [isLoading, setIsLoading] = useState(enabled && !cached);

  useEffect(() => {
    if (!enabled) {
      setIsLoading(false);
      return;
    }

    let cancelled = false;

    async function load() {
      // With cache: keep showing it and soft-revalidate.
      if (!readClientCache()) setIsLoading(true);
      try {
        const blob = await fetchPortfolioShared(forceRefresh);
        if (cancelled) return;
        if (blob) setSections(blob.sections);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }

    void load();
    return () => {
      cancelled = true;
    };
  }, [forceRefresh, enabled]);

  /** URLs para galería home: solo imágenes NO horizontales (las horizontales solo en /portfolio) */
  const allImageUrls = sections
    .flatMap((s) => s.urls)
    .map((raw) => parsePortfolioUrl(raw?.trim() ?? ""))
    .filter((e) => e.url && !e.wide && isCloudinaryImage(e.url))
    .map((e) => e.url);

  /** Mostrar enlace /portfolio en el header solo si hay contenido público Cloudinary */
  const showPortfolioNavLink = useMemo(() => {
    if (isLoading && sections.length === 0) return false;
    return sections.some((s) =>
      s.urls.some((u) => {
        const p = parsePortfolioUrl(u.trim());
        return Boolean(p.url && isCloudinaryImage(p.url));
      }),
    );
  }, [isLoading, sections]);

  return { sections, isLoading, allImageUrls, showPortfolioNavLink };
}
